import React, { useEffect, useState } from "react";
import { Building2, Heart, Users } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Page, Card, StatCard, PrimaryButton } from "../components/UI";
import api from "../lib/api";
import { toast } from "sonner";

export default function EAP() {
  const [report, setReport] = useState(null);
  const [form, setForm] = useState({ employer_id: "demo-employer", mood_score: 5, wellbeing_score: 5, notes: "" });

  const load = () => api.get("/eap/report").then(({ data }) => setReport(data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/eap/checkin", { ...form, mood_score: parseInt(form.mood_score), wellbeing_score: parseInt(form.wellbeing_score) });
      toast.success("Check-in recorded");
      setForm({ ...form, notes: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const trendData = (report?.trend || []).slice(-14).map((c, i) => ({
    day: i + 1,
    mood: c.mood_score,
    wellbeing: c.wellbeing_score,
  }));

  return (
    <Page title="Employer / EAP Portal" subtitle="Aggregate, de-identified utilization and ROI.">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <StatCard label="Avg Mood" value={report?.avg_mood ?? "—"} icon={Heart} accent="var(--mf-primary)" delay={0.05} testid="eap-avg-mood" />
        <StatCard label="Avg Wellbeing" value={report?.avg_wellbeing ?? "—"} icon={Heart} accent="var(--mf-secondary)" delay={0.1} testid="eap-avg-wellbeing" />
        <StatCard label="Check-ins" value={report?.total_checkins ?? 0} icon={Users} accent="var(--mf-accent)" delay={0.15} testid="eap-checkins" />
        <StatCard label="Utilization" value={`${Math.round((report?.utilization ?? 0) * 100)}%`} icon={Building2} accent="#B45253" delay={0.2} testid="eap-utilization" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card delay={0.25} testid="eap-trend">
          <h3 className="font-serif text-xl mb-4">Wellbeing Trend (14d)</h3>
          <div className="h-64">
            {trendData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm" style={{ color: "var(--mf-text-muted)" }}>No check-ins yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="day" stroke="var(--mf-text-muted)" style={{ fontSize: "12px" }} />
                  <YAxis stroke="var(--mf-text-muted)" style={{ fontSize: "12px" }} domain={[0, 10]} />
                  <Tooltip contentStyle={{ background: "var(--mf-surface)", border: "none", borderRadius: "12px" }} />
                  <Line type="monotone" dataKey="mood" stroke="var(--mf-primary)" strokeWidth={2.5} />
                  <Line type="monotone" dataKey="wellbeing" stroke="var(--mf-secondary)" strokeWidth={2.5} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card delay={0.3} testid="eap-checkin-form">
          <h3 className="font-serif text-xl mb-4">Employee Check-In</h3>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: "var(--mf-text-muted)" }}>Mood (1-10)</label>
              <input type="range" min={1} max={10} value={form.mood_score} onChange={set("mood_score")} className="w-full" data-testid="eap-mood-slider" />
              <div className="text-center font-serif text-2xl" style={{ color: "var(--mf-primary)" }}>{form.mood_score}</div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: "var(--mf-text-muted)" }}>Wellbeing (1-10)</label>
              <input type="range" min={1} max={10} value={form.wellbeing_score} onChange={set("wellbeing_score")} className="w-full" data-testid="eap-wellbeing-slider" />
              <div className="text-center font-serif text-2xl" style={{ color: "var(--mf-secondary)" }}>{form.wellbeing_score}</div>
            </div>
            <textarea placeholder="Notes (optional)" value={form.notes} onChange={set("notes")} rows={3} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm resize-none" style={{ background: "var(--mf-muted)" }} data-testid="eap-notes"></textarea>
            <PrimaryButton type="submit" testid="eap-submit-btn">Submit Check-In</PrimaryButton>
          </form>
        </Card>
      </div>
    </Page>
  );
}
